import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import './databases.css';

function PropertiesCard() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [properties, setProperties] = useState([]);
  const [propertyAttributes, setPropertyAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [propertySearch, setPropertySearch] = useState('');
  const [propertySortKey, setPropertySortKey] = useState('');
  const [propertySortOrder, setPropertySortOrder] = useState('asc');
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showAddPropertyAttributeModal, setShowAddPropertyAttributeModal] = useState(false);
  const [showDeletePropertyAttributeModal, setShowDeletePropertyAttributeModal] = useState(false);
     const [newAttribute, setNewAttribute] = useState('');
   const [newAttributeType, setNewAttributeType] = useState('string');
   const [newAttributeRelationTable, setNewAttributeRelationTable] = useState('');
   const [attributeToDelete, setAttributeToDelete] = useState('');
   const [availableDatabases, setAvailableDatabases] = useState([]);
   const [relationOptions, setRelationOptions] = useState({});
   const [editProperty, setEditProperty] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Column visibility and resizing state
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [columnWidths, setColumnWidths] = useState({});
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [dbColumns, setDbColumns] = useState([]);
  const [showExcelUploadModal, setShowExcelUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);

  const [newProperty, setNewProperty] = useState({
    property_name: '',
    location: '',
    unit_count: '',
    property_manager: ''
  });

  const MAX_VISIBLE_COLUMNS = 10; // Increased to show more columns
  
         useEffect(() => {
       const storedUser = localStorage.getItem('username');
       if (!storedUser) {
         navigate('/');
       } else {
         setUsername(storedUser);
       }
       fetchData();
       fetchAttributes('properties', setPropertyAttributes);
       fetchAttributeTypes('properties');
       fetchDbColumns();
       fetchAvailableDatabases();
     }, [navigate]);

    const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('Fetching properties data from:', `${API_BASE_URL}/api/properties`);
      const [propRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/properties`),
        ]);
      
      console.log('Properties response status:', propRes.status);
      
      if (!propRes.ok) {
        console.error('Properties API call failed:', propRes.status);
        throw new Error(`HTTP error! status: ${propRes.status}`);
      }
      const propData = await propRes.json();
      
      console.log('Fetched properties data:', propData.length, 'properties');
      
      // Fetch unit types for each property
      const propertiesWithUnitTypes = await Promise.all(
        propData.map(async (property) => {
          try {
            const unitTypesRes = await fetch(`${API_BASE_URL}/api/properties/${property.id}/unit-types`);
            if (unitTypesRes.ok) {
              const unitTypes = await unitTypesRes.json();
              return { ...property, unit_types: unitTypes };
            }
            return { ...property, unit_types: [] };
          } catch (error) {
            console.error(`Error fetching unit types for property ${property.id}:`, error);
            return { ...property, unit_types: [] };
          }
        })
      );
      
      // Transform backend data from snake_case to Title Case for frontend display
      const transformedProperties = propertiesWithUnitTypes.map(property => {
        const transformed = {};
        // Preserve id for navigation and updates
        transformed.id = property.id;
        // Preserve unit_types array
        transformed.unit_types = property.unit_types || [];
        Object.keys(property).forEach(key => {
          // Skip unit_types as we already handled it
          if (key === 'unit_types') return;
          
          let displayKey;
          switch(key) {
            case 'property_name':
              displayKey = 'Property Name';
              break;
            case 'address':
              displayKey = 'Address';
              break;
            case 'unit_count':
              displayKey = 'Unit Count';
              break;
            case 'property_manager':
              displayKey = 'Property Manager';
              break;
            case 'created_at':
              displayKey = 'Created At';
              break;
            default:
              // For custom attributes, convert snake_case to Title Case
              displayKey = key.split('_').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ');
          }
          transformed[displayKey] = property[key];
        });
        return transformed;
      });
      
      setProperties(transformedProperties);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(`Failed to fetch data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

     const fetchAttributes = async (tableName, setAttributes) => {
     try {
       const response = await fetch(`${API_BASE_URL}/api/${tableName}/attributes`);
       if (!response.ok) {
         throw new Error(`HTTP error! status: ${response.status}`);
       }
       const data = await response.json();
       // Extract the attributes array from the response
       setAttributes(data.attributes || []);
     } catch (error) {
       console.error(`Error fetching ${tableName} attributes:`, error);
       setError(`Failed to fetch ${tableName} attributes: ${error.message}`);
     }
   };

   const fetchAttributeTypes = async (tableName) => {
     try {
       const response = await fetch(`${API_BASE_URL}/api/${tableName}/attribute-types`);
       if (!response.ok) {
         throw new Error(`HTTP error! status: ${response.status}`);
       }
       const data = await response.json();
       setAttributeTypes(data);
       // Fetch relation options for any relation-type attributes
       const types = data.attribute_types || {};
       Object.entries(types).forEach(([, typeVal]) => {
         if (typeof typeVal === 'string' && typeVal.startsWith('relation:')) fetchRelationOptions(typeVal.split(':')[1]);
       });
     } catch (error) {
       console.error(`Error fetching ${tableName} attribute types:`, error);
     }
   };

  const fetchAvailableDatabases = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/available-databases`);
      if (!res.ok) return;
      const data = await res.json();
      setAvailableDatabases(data.databases || []);
    } catch (e) { console.error('Error fetching available databases:', e); }
  };

  const fetchRelationOptions = async (tableName) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/${tableName}/relation-options`);
      if (!res.ok) return;
      const data = await res.json();
      setRelationOptions(prev => ({ ...prev, [tableName]: data.options || [] }));
    } catch (e) { console.error('Error fetching relation options:', e); }
  };

  // Fetch actual DB columns to allow adding them from Column Settings
  const fetchDbColumns = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/properties/columns`);
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const data = await res.json();
      setDbColumns(Array.isArray(data.columns) ? data.columns : []);
    } catch (e) {
      console.error('Error fetching DB columns:', e);
    }
  };

  const handlePropertySort = (key) => {
    if (propertySortKey === key) {
      setPropertySortOrder(propertySortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setPropertySortKey(key);
      setPropertySortOrder('asc');
    }
  };

  const sortedFilteredProperties = [...properties]
    .filter(row =>
      Object.values(row).some(value =>
        value && value.toString().toLowerCase().includes(propertySearch.toLowerCase())
      )
    )
    .sort((a, b) => {
      if (!propertySortKey) return 0;
      const aValue = a[propertySortKey] || '';
      const bValue = b[propertySortKey] || '';
      return propertySortOrder === 'asc'
        ? aValue.toString().localeCompare(bValue.toString())
        : bValue.toString().localeCompare(aValue.toString());
    });

  // Pagination logic
  const totalPages = Math.ceil(sortedFilteredProperties.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedProperties = sortedFilteredProperties.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [propertySearch]);

  const addProperty = async () => {
    // Validate required fields
    if (!newProperty.property_name || !newProperty.location || !newProperty.property_manager) {
      setError('Please fill in all required fields');
      return;
    }
    
    const unitCount = Number(newProperty.unit_count);
    if (isNaN(unitCount) || unitCount < 0) {
      setError('Unit Count must be a valid number');
      return;
    }

    setLoading(true);
    try {
      // Create the property object for backend (snake_case format)
      const propertyForBackend = {
        property_name: newProperty.property_name,
        address: newProperty.location,
        unit_count: unitCount,
        property_manager: newProperty.property_manager
      };

      // Add any additional attributes
      (Array.isArray(propertyAttributes) ? propertyAttributes.slice(4) : []).forEach(attr => {
        const dbColumn = attr.toLowerCase().replace(/[ -]/g, '_');
        propertyForBackend[dbColumn] = newProperty[attr.toLowerCase().replace(/[ -]/g, '_')] || '';
      });

      // Transform existing properties to backend format
      const transformedProperties = properties.map(row => ({
        id: row.id,
        property_name: row['Property Name'] || '',
        address: row['Address'] || '',
        unit_count: Number(row['Unit Count']) || 0,
        property_manager: row['Property Manager'] || '',
        ...(Array.isArray(propertyAttributes) ? propertyAttributes.slice(4) : []).reduce((acc, attr) => {
          const dbColumn = attr.toLowerCase().replace(/[ -]/g, '_');
          acc[dbColumn] = row[attr] || '';
          return acc;
        }, {})
      }));

      // Add to backend (send ONLY the new property object)
      const response = await fetch(`${API_BASE_URL}/api/properties`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(propertyForBackend)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      // Reset form and refresh data
      const resetProperty = {
        property_name: '',
        location: '',
        unit_count: '',
        property_manager: ''
      };
      // Also clear any dynamic attributes
      (Array.isArray(propertyAttributes) ? propertyAttributes.slice(4) : []).forEach(attr => {
        const fieldName = attr.toLowerCase().replace(/[ -]/g, '_');
        resetProperty[fieldName] = '';
      });
      setNewProperty(resetProperty);
      setShowPropertyModal(false);
      setError('');
      await fetchData();
    } catch (error) {
      console.error('Error adding property:', error);
      setError(`Failed to add property: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCSVUpload = async () => {
    if (!uploadFile) {
      setError('Please select a file to upload');
      return;
    }

    setLoading(true);
    setUploadProgress('Uploading...');
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      // Add timeout to the fetch request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout

      const response = await fetch(`${API_BASE_URL}/api/properties/upload-csv`, {
        method: 'POST',
        body: formData,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const result = await response.json();
      
      if (response.ok) {
        setUploadProgress(`Upload completed! ${result.success_count} records imported successfully.`);
        if (result.new_attributes_added && result.new_attributes_added.length > 0) {
          setUploadProgress(prev => prev + ` New attributes added: ${result.new_attributes_added.join(', ')}`);
        }
        if (result.error_count > 0) {
          setUploadProgress(prev => prev + ` ${result.error_count} errors occurred.`);
        }
        await fetchData();
        await fetchAttributeTypes('properties');
        setTimeout(() => {
          setShowExcelUploadModal(false);
          setUploadFile(null);
          setUploadProgress(null);
        }, 3000);
      } else {
        setError(result.error || 'Upload failed');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('Upload timed out. Please try with a smaller file or check your connection.');
      } else {
        setError('Upload failed: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleEditProperty = (row) => {
    // Navigate to the dedicated edit page
    navigate(`/property/${row.id}`);
  };


  const saveProperty = async (updatedProp) => {
    if (!updatedProp['Property Name'] || !updatedProp['Address'] || !updatedProp['Property Manager']) {
      setError('Please fill in all required fields');
      return;
    }
    const unitCount = Number(updatedProp['Unit Count']);
    if (isNaN(unitCount) || unitCount < 0) {
      setError('Unit Count must be a valid number');
      return;
    }
    updatedProp['Unit Count'] = unitCount;

    const newData = properties.map(row => row.id === updatedProp.id ? updatedProp : row);
    setProperties(newData);
    setEditProperty(null);
    setShowPropertyModal(false);
    setError('');
    await updatePropertiesBackend(newData);
  };
  
  const removeProperty = async (id) => {
    if (window.confirm('Are you sure you want to delete this property?')) {
      const newData = properties.filter(row => row.id !== id);
      setProperties(newData);
      setShowPropertyModal(false);
      setEditProperty(null);
      await updatePropertiesBackend(newData);
    }
  };


  const updatePropertiesBackend = async (newData) => {
    setLoading(true);
    try {
      // Persist changes one-by-one using PUT /api/properties/:id
      for (const row of newData) {
        const payload = {
          property_name: row['Property Name'] || '',
          address: row['Address'] || '',
          unit_count: Number(row['Unit Count']) || 0,
          property_manager: row['Property Manager'] || ''
        };
        (Array.isArray(propertyAttributes) ? propertyAttributes.slice(4) : []).forEach(attr => {
          const key = attr.toLowerCase().replace(/[ -]/g, '_');
          payload[key] = row[attr] || '';
        });
        const resp = await fetch(`${API_BASE_URL}/api/properties/${row.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!resp.ok) {
          const errorData = await resp.json();
          throw new Error(errorData.error || `HTTP error! status: ${resp.status}`);
        }
      }
      await fetchData();
    } catch (error) {
      console.error('Error updating properties:', error);
      setError(`Failed to update properties: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

     const addAttribute = async (tableName, setAttributes, setShowModal) => {
     if (!newAttribute) {
       setError('Attribute name is required');
       return;
     }
     setLoading(true);
     try {
       const payload = { attribute: newAttribute, type: newAttributeType };
       if (newAttributeType === 'relation') payload.relation_table = newAttributeRelationTable;
       const response = await fetch(`${API_BASE_URL}/api/${tableName}/attributes`, {
         method: 'POST',
         headers: { 'Content-Type': 'application/json' },
         body: JSON.stringify(payload)
       });
       if (!response.ok) {
         const errorData = await response.json();
         throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
       }
       const data = await response.json();
       // Refetch attributes to get the updated list
       await fetchAttributes(tableName, setAttributes);
       setNewAttribute('');
       setNewAttributeType('string');
       setShowModal(false);
       setError('');
       await fetchData();
     } catch (error) {
       console.error(`Error adding attribute to ${tableName}:`, error);
       setError(`Failed to add attribute: ${error.message}`);
     } finally {
       setLoading(false);
     }
   };

  const deleteAttribute = async (tableName, attribute, setAttributes, setShowModal) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/${tableName}/attributes/${attribute}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      // Refetch attributes to get the updated list
      await fetchAttributes(tableName, setAttributes);
      setAttributeToDelete('');
      setShowModal(false);
      setError('');
      await fetchData();
    } catch (error) {
      console.error(`Error deleting attribute from ${tableName}:`, error);
      setError(`Failed to delete attribute: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getPropertyName = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    return property ? property['Property Name'] : 'Unknown Property';
  };


  // Filter out hidden columns and get visible attributes
  const visiblePropertyAttributes = (Array.isArray(propertyAttributes) ? propertyAttributes : []).filter(attr => !hiddenColumns.includes(attr));

  // Column management functions
  const toggleColumnVisibility = (attr) => {
    setHiddenColumns(prev => 
      prev.includes(attr) 
        ? prev.filter(col => col !== attr)
        : [...prev, attr]
    );
  };

  const resetColumnWidths = () => {
    setColumnWidths({});
  };

  const handleColumnResize = (attr, newWidth) => {
    setColumnWidths(prev => ({
      ...prev,
      [attr]: Math.max(100, newWidth) // Minimum width of 100px
    }));
  };

  const getColumnWidth = (attr) => {
    return columnWidths[attr] || 'auto';
  };

  // Format currency values
  const formatCurrency = (value) => {
    if (isNaN(Number(value))) return value;
    const n = Number(value);
    const formatted = n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    return n < 0 ? `(${formatted.replace('-', '')})` : formatted;
  };

     // State for attribute types
   const [attributeTypes, setAttributeTypes] = useState({});

   // Check if a field should be formatted as currency
   const isCurrencyField = (attr) => {
     if (attributeTypes[attr] === 'currency') return true;
     return /price|rent|revenue|expense/i.test(attr) || 
            attr === 'Average Unit Rent' || 
            attr === 'Annual Rental Income' || 
            attr === 'Annual Rent';
   };

   const getAttrType = (attr) => {
     const types = attributeTypes.attribute_types || attributeTypes || {};
     return types[attr] || '';
   };

   const isRelationField = (attr) => {
     return getAttrType(attr).startsWith('relation:');
   };

   const getRelationTable = (attr) => {
     const t = getAttrType(attr);
     return t.startsWith('relation:') ? t.split(':')[1] : null;
   };

   // Check if a field should be formatted as number
   const isNumberField = (attr) => {
     return getAttrType(attr) === 'number' || attributeTypes[attr] === 'number' || attr === 'Unit Count';
   };

  return (
    <>
      <div className="notion-card">
            <div className="notion-header">
              <h1 className="notion-title">Properties Database</h1>
              <div className="notion-actions">
                <input
                  type="text"
                  value={propertySearch}
                  onChange={(e) => setPropertySearch(e.target.value)}
                  placeholder="🔍 Search Properties"
                  className="search"
                />
                <button
                  onClick={() => handlePropertySort('Property Name')}
                  className="notion-button"
                >
                  Sort by Name {propertySortKey === 'Property Name' && (propertySortOrder === 'asc' ? '▲' : '▼')}
                </button>
                <button
                  onClick={() => navigate('/properties/new-wizard')}
                  className="notion-button notion-button-primary"
                  disabled={loading}
                >
                  + Add Property
                </button>
                <button
                  onClick={() => setShowAddPropertyAttributeModal(true)}
                  className="notion-button"
                  disabled={loading}
                >
                  + Add Attribute
                </button>
                                 <button
                   onClick={() => setShowDeletePropertyAttributeModal(true)}
                   className="notion-button"
                   disabled={loading || (Array.isArray(propertyAttributes) ? propertyAttributes.length : 0) <= 4}
                 >
                   - Delete Attribute
                 </button>
                                   <button
                   onClick={() => setShowExcelUploadModal(true)}
                   className="notion-button"
                   disabled={loading}
                 >
                   📊 Upload CSV
                 </button>
                 <button
                   onClick={() => setShowColumnSettings(true)}
                   className="notion-button"
                   disabled={loading}
                 >
                   ⚙️ Column Settings
                 </button>
              </div>
            </div>
            
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            
            {loading && (
              <div className="loading">
                <div style={{ marginRight: '10px' }}>🔄</div>
                <span>Loading properties data...</span>
              </div>
            )}
            
            {showPropertyModal && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <h2 className="notion-modal-title">{editProperty ? 'Edit Property' : 'Add New Property'}</h2>
                  {error && (
                    <div className="error-message">
                      {error}
                    </div>
                  )}
                  <div className="notion-form">
                    <form onSubmit={(e) => { e.preventDefault(); editProperty ? saveProperty(editProperty) : addProperty(); }}>
                      {(Array.isArray(propertyAttributes) ? propertyAttributes : []).map(attr => {
                        const getFieldName = () => {
                          if (attr === 'Property Name') return 'property_name';
                          if (attr === 'Address') return 'location';
                          if (attr === 'Unit Count') return 'unit_count';
                          if (attr === 'Property Manager') return 'property_manager';
                          return attr.toLowerCase().replace(' ', '_');
                        };
                        const fieldVal = editProperty ? (editProperty[attr] || '') : (newProperty[getFieldName()] || '');
                        const handleChange = (val) => {
                          if (editProperty) setEditProperty({ ...editProperty, [attr]: val });
                          else setNewProperty({ ...newProperty, [getFieldName()]: val });
                        };
                        if (isRelationField(attr)) {
                          const relTable = getRelationTable(attr);
                          const options = relationOptions[relTable] || [];
                          if (options.length === 0 && relTable) fetchRelationOptions(relTable);
                          return (
                            <div key={attr} className="form-row">
                              <select value={fieldVal} onChange={e => handleChange(e.target.value)}>
                                <option value="">Select {attr}...</option>
                                {options.map(opt => <option key={opt.id} value={opt.name}>{opt.name}</option>)}
                              </select>
                            </div>
                          );
                        }
                        return (
                          <div key={attr} className="form-row">
                            <input
                              type={isNumberField(attr) || isCurrencyField(attr) ? 'number' : 'text'}
                              placeholder={attr}
                              value={fieldVal}
                              onChange={e => handleChange(e.target.value)}
                              required={['Property Name', 'Address', 'Property Manager'].includes(attr)}
                            />
                          </div>
                        );
                      })}
                      <div className="form-actions">
                        <button type="submit" className="notion-button notion-button-primary" disabled={loading}>
                          {loading ? 'Saving...' : editProperty ? 'Update' : 'Create'}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowPropertyModal(false); setEditProperty(null); }}
                          className="notion-button"
                          disabled={loading}
                        >
                          Cancel
                        </button>
                        {editProperty && (
                          <button
                            type="button"
                            onClick={() => { removeProperty(editProperty.id); }}
                            className="notion-button notion-button-danger"
                            disabled={loading}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {showExcelUploadModal && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <h2 className="notion-modal-title">Upload CSV File</h2>
                  <div className="excel-upload-info">
                    <p><strong>Instructions:</strong></p>
                    <ul>
                      <li>Upload a CSV file (.csv) with property data</li>
                      <li>Column headers should match existing attributes or will be added as new attributes</li>
                      <li>Required columns: Property Name, Address, Unit Count, Property Manager</li>
                      <li>Any additional columns will be added as new attributes</li>
                      <li>Make sure your CSV uses commas as separators</li>
                    </ul>
                  </div>
                  <div className="notion-modal-field">
                    <label>Select CSV File</label>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                      className="notion-input"
                    />
                  </div>
                  {uploadProgress && (
                    <div className="upload-progress">
                      {uploadProgress}
                    </div>
                  )}
                  <div className="notion-modal-actions">
                    <button 
                      className="notion-button notion-button-primary" 
                      onClick={handleCSVUpload} 
                      disabled={loading || !uploadFile}
                    >
                      {loading ? 'Uploading...' : 'Upload'}
                    </button>
                    <button 
                      className="notion-button" 
                      onClick={() => {
                        setShowExcelUploadModal(false);
                        setUploadFile(null);
                        setUploadProgress(null);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

                         {showAddPropertyAttributeModal && (
               <div className="modal-overlay">
                 <div className="modal-content">
                   <h2 className="notion-modal-title">Add New Property Attribute</h2>
                   <div className="notion-modal-field">
                     <label className="notion-label">Attribute Name</label>
                     <input
                       type="text"
                       value={newAttribute}
                       onChange={(e) => setNewAttribute(e.target.value)}
                       className="notion-input"
                       placeholder="Enter attribute name"
                     />
                   </div>
                   <div className="notion-modal-field">
                     <label className="notion-label">Data Type</label>
                     <select
                       value={newAttributeType}
                       onChange={(e) => { setNewAttributeType(e.target.value); if (e.target.value !== 'relation') setNewAttributeRelationTable(''); }}
                       className="notion-select"
                     >
                       <option value="string">Text (String)</option>
                       <option value="number">Number</option>
                       <option value="currency">Currency</option>
                       <option value="relation">Relation (Link to Database)</option>
                     </select>
                   </div>
                   {newAttributeType === 'relation' && (
                     <div className="notion-modal-field">
                       <label className="notion-label">Link to Database</label>
                       <select value={newAttributeRelationTable} onChange={e => setNewAttributeRelationTable(e.target.value)} className="notion-select">
                         <option value="">Select a database...</option>
                         {availableDatabases.filter(db => db.table_name !== 'properties').map(db => (
                           <option key={db.table_name} value={db.table_name}>{db.icon} {db.display_name}</option>
                         ))}
                       </select>
                     </div>
                   )}
                   <div className="notion-modal-actions">
                     <button
                       onClick={() => addAttribute('properties', setPropertyAttributes, setShowAddPropertyAttributeModal)}
                       className="notion-button"
                       disabled={loading || (newAttributeType === 'relation' && !newAttributeRelationTable)}
                     >
                       Add Attribute
                     </button>
                     <button
                       onClick={() => setShowAddPropertyAttributeModal(false)}
                       className="notion-button notion-button-secondary"
                       disabled={loading}
                     >
                       Cancel
                     </button>
                   </div>
                 </div>
               </div>
             )}
            {showDeletePropertyAttributeModal && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <h2 className="notion-modal-title">Delete Property Attribute</h2>
                  <div className="notion-modal-field">
                    <label className="notion-label">Select Attribute to Delete</label>
                    <select
                      value={attributeToDelete}
                      onChange={(e) => setAttributeToDelete(e.target.value)}
                      className="notion-select"
                    >
                      <option value="">Select an attribute</option>
                      {(Array.isArray(propertyAttributes) ? propertyAttributes.slice(4) : []).map(attr => (
                        <option key={attr} value={attr}>{attr}</option>
                      ))}
                    </select>
                  </div>
                  <div className="notion-modal-actions">
                    <button
                      onClick={() => deleteAttribute('properties', attributeToDelete, setPropertyAttributes, setShowDeletePropertyAttributeModal)}
                      className="notion-button notion-button-danger"
                      disabled={loading || !attributeToDelete}
                    >
                      Delete Attribute
                    </button>
                    <button
                      onClick={() => setShowDeletePropertyAttributeModal(false)}
                      className="notion-button notion-button-secondary"
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
                             </div>
             )}
             {showColumnSettings && (
               <div className="modal-overlay">
                 <div className="modal-content">
                   <h2 className="notion-modal-title">Column Settings</h2>
                   <button className="modal-close-x" aria-label="Close" onClick={() => setShowColumnSettings(false)}>×</button>
                   <div style={{ marginBottom: '20px' }}>
                     <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Column Visibility</h3>
                     <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                       {(Array.isArray(propertyAttributes) ? propertyAttributes : []).map(attr => (
                         <label key={attr} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                           <input
                             type="checkbox"
                             checked={!hiddenColumns.includes(attr)}
                             onChange={() => toggleColumnVisibility(attr)}
                           />
                           {attr}
                         </label>
                       ))}
                     </div>
                   </div>
                   <div style={{ marginBottom: '20px' }}>
                     <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Add Columns (from DB)</h3>
                     <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
                       {dbColumns
                         .filter(col => !['id'].includes(col))
                         .map(col => {
                           const title = col.split('_').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
                           const alreadyListed = (propertyAttributes || []).includes(title);
                           return (
                             <button
                               key={col}
                               className="notion-button"
                               disabled={alreadyListed}
                               onClick={() => {
                                 const next = Array.isArray(propertyAttributes) ? [...propertyAttributes] : [];
                                 if (!alreadyListed) {
                                   next.push(title);
                                   setPropertyAttributes(next);
                                   setHiddenColumns(prev => prev.filter(h => h !== title));
                                 }
                               }}
                             >
                               {alreadyListed ? `Added: ${title}` : `Add ${title}`}
                             </button>
                           );
                         })}
                     </div>
                   </div>
                  
                 </div>
               </div>
             )}
             <div className="notion-table-container">
              {loading && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px', 
                  color: '#6c757d',
                  fontSize: '16px'
                }}>
                  Loading properties...
                </div>
              )}
              {!loading && (
                <table className="notion-table">
                                     <thead>
                     <tr>
                       {visiblePropertyAttributes.map(attr => (
                         <th 
                           key={attr} 
                           className="notion-th" 
                           onClick={() => handlePropertySort(attr)}
                           style={{ 
                             width: getColumnWidth(attr),
                             minWidth: '100px',
                             position: 'relative'
                           }}
                         >
                           <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                             <span>{attr} {propertySortKey === attr && (propertySortOrder === 'asc' ? '▲' : '▼')}</span>
                             <div 
                               className="column-resize-handle"
                               style={{
                                 position: 'absolute',
                                 right: '0',
                                 top: '0',
                                 bottom: '0',
                                 width: '4px',
                                 cursor: 'col-resize',
                                 background: 'transparent'
                               }}
                               onMouseDown={(e) => {
                                 e.preventDefault();
                                 const startX = e.clientX;
                                 const startWidth = columnWidths[attr] || 150;
                                 
                                 const handleMouseMove = (moveEvent) => {
                                   const deltaX = moveEvent.clientX - startX;
                                   const newWidth = Math.max(100, startWidth + deltaX);
                                   handleColumnResize(attr, newWidth);
                                 };
                                 
                                 const handleMouseUp = () => {
                                   document.removeEventListener('mousemove', handleMouseMove);
                                   document.removeEventListener('mouseup', handleMouseUp);
                                 };
                                 
                                 document.addEventListener('mousemove', handleMouseMove);
                                 document.addEventListener('mouseup', handleMouseUp);
                               }}
                             />
                           </div>
                         </th>
                       ))}
                       <th className="notion-th" style={{ width: '150px' }}>Unit Types</th>
                       <th className="notion-th" style={{ width: '80px' }}>Actions</th>
                     </tr>
                   </thead>
                <tbody>
                                     {paginatedProperties.map(row => (
                     <tr key={row.id} className="notion-row">
                       {visiblePropertyAttributes.map(attr => (
                         <td 
                           key={attr} 
                           className="notion-td"
                           style={{ 
                             width: getColumnWidth(attr),
                             minWidth: '100px'
                           }}
                                                                              >
                             {(() => {
                               if (isCurrencyField(attr)) return formatCurrency(row[attr]);
                               if (isNumberField(attr)) {
                                 const numValue = parseFloat(row[attr]);
                                 return isNaN(numValue) ? (row[attr] || '') : numValue.toLocaleString();
                               }
                               return (row[attr] || '');
                             })()}
                           </td>
                       ))}
                       <td className="notion-td" style={{ width: '150px' }}>
                         {row.unit_types && row.unit_types.length > 0 ? (
                           <div className="unit-types-summary">
                             {row.unit_types.slice(0, 2).map((unitType, index) => (
                               <div key={index} className="unit-type-badge">
                                 {unitType.unit_count}x {unitType.type_name}
                               </div>
                             ))}
                             {row.unit_types.length > 2 && (
                               <div className="unit-type-more">
                                 +{row.unit_types.length - 2} more
                               </div>
                             )}
                           </div>
                         ) : (
                           <span className="no-unit-types">No unit types</span>
                         )}
                       </td>
                       <td className="notion-td" style={{ width: '80px' }}>
                        <button
                          onClick={() => handleEditProperty(row)}
                          className="notion-action"
                          disabled={loading}
                        >
                          ⋮
                        </button>
                      </td>
                    </tr>
                  ))}
                  {paginatedProperties.length === 0 && !loading && sortedFilteredProperties.length === 0 && (
                    <tr>
                      <td colSpan={visiblePropertyAttributes.length + 2} className="notion-td" style={{ textAlign: 'center', padding: 0, border: 'none' }}>
                        <div className="db-empty-state" style={{ border: 'none', borderRadius: 0 }}>
                          <div className="db-empty-state-icon">🏢</div>
                          <div className="db-empty-state-title">No properties yet</div>
                          <div className="db-empty-state-text">Add your first property to start building your portfolio.</div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              )}

              {/* Pagination Controls */}
              <div className="db-pagination">
                <div className="db-pagination-left">
                  <label>Rows per page:</label>
                  <select value={rowsPerPage} onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}>
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <div className="db-pagination-center">
                  Showing {sortedFilteredProperties.length === 0 ? 0 : startIndex + 1} to {Math.min(endIndex, sortedFilteredProperties.length)} of {sortedFilteredProperties.length} properties
                </div>
                <div className="db-pagination-right">
                  <button className="db-pagination-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (currentPage <= 3) pageNum = i + 1;
                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = currentPage - 2 + i;
                    return (
                      <button key={pageNum} className={`db-pagination-btn ${currentPage === pageNum ? 'active' : ''}`} onClick={() => handlePageChange(pageNum)}>{pageNum}</button>
                    );
                  })}
                  <button className="db-pagination-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
                </div>
              </div>
            </div>
          </div>
        </>
      );
}

export default PropertiesCard;
